import { useStandardErrorState } from "../../../entities/view-manager/error";
import { useState, useEffect } from "react";
import { IStormDoorModel } from "../../../entities-generated/models/storm-door/storm-door-model";
import { BwjPageContent } from "../../../framework/components/bwj-page-content";
import { BwjPageHeader } from "../../../framework/components/bwj-page-header";
import { BwjViewState, BwjViewManager } from "../../../framework/components/bwj-view-manager";
import { ProductTileContainer } from "../../../app-shop/shop/components/product-tile-container";
import { ShoppingIntroDialog } from "../../../app-shop/shop/components/shop-intro-dialog";
import { StormDoorTile } from "../../components/st-door-tile";
import { BrowseApiClient } from "../../../services/browse";
import { StormDoorDetailDialog } from "../../components/st-door-detail-dialog";
import { LocalStorageService } from "../../../framework/services/local-storage";
import { DAY_IN_MIILISECONDS } from "../../../framework/utils/date-time";
import * as formService from '../../form-services/customer-enrollment';
import { ToastService } from "../../../framework/services/toast";
import { MessageBarType } from "@fluentui/react";

const LS_KEY = 'Bwj.Storm.Browse.LastMeasureRequest';

const setLastMeasureRequest = () => {
  const now = (new Date()).getTime();
  LocalStorageService.set(LS_KEY, now);
};

const getLastMeasureRequest = () => {
  const t = LocalStorageService.get<number>(LS_KEY);
  if(t === undefined) { return null; }
  
  const now = (new Date()).getTime();
  const delta = now - t;
  if(delta > (DAY_IN_MIILISECONDS * 2)) { return null; }

  return new Date(t);
}

const subscriberId = 'Page.Browse';

export const PageBrowse: React.FunctionComponent = () => {
  const errorState = useStandardErrorState();
  const [viewState, setViewState] = useState(BwjViewState.Loading);
  const [showExplainerDialog, setShowExplainerDialog] = useState(false);
  const [doors, setDoors] = useState<IStormDoorModel[]>([]);
  const [displayedDoorDetails, setDisplayedDoorDetails] = useState<IStormDoorModel | undefined>(undefined);
  const formModel = formService.useFormModel(subscriberId);
  const [measurementLastRequested, setMeasurementLastRequested] = useState(getLastMeasureRequest());


  useEffect(() => {
    setViewState(BwjViewState.Loading);

    BrowseApiClient.getStormDoorCatalog()
    .then(resp => {
      setDoors(resp);
      const vs = resp.length > 0 ? BwjViewState.Ready : BwjViewState.Error;
      setViewState(vs);
    })
    .catch(() => {
      setViewState(BwjViewState.Error);
    });

  }, []);

  const onViewDoorDetails = (door: IStormDoorModel) => {
    setDisplayedDoorDetails(door);
  }

  const onCloseDoorDetails = () => {
    setDisplayedDoorDetails(undefined);
  }

  const onCloseExplainerDialog = () => {
    setShowExplainerDialog(false);
  }

  const onMeasurementRequestSubmitted = () => {
    setViewState(BwjViewState.Loading);

    const request = formService.toCreateProjectRequest();
    BrowseApiClient.createNewProject(request)
    .then(() => {
      setLastMeasureRequest();
      setMeasurementLastRequested(new Date());
      ToastService.show(MessageBarType.success, 'Thank you! We\'ll be in touch with you as soon as possible');
    })
    .catch(() => {
      ToastService.show(MessageBarType.error, 'Error; please call us at (585) 576-1654', 30000);
    })
    .finally(() => setViewState(BwjViewState.Ready));
  };

  return (
    <>
    <ShoppingIntroDialog
      showDialog={showExplainerDialog}
      title={'Why can\'t you just tell me the price of the door?'}
      richContent={<>
      <p>
        Good question &ndash; after all, home improvement stores clearly advertise the prices of their doors, right?
      </p>
      <p>
        <strong>Well, not really.</strong>  After all, you don&#39;t just want a new door... you want to make a new door <em>part of your home</em>. We&#39;ll go out on a limb and say you probably want that door to have a handle.  At big-box stores, installation, sometimes even necessary hardware, are all extra charges.  How much extra?  <em>They literally make you pay to find out</em>.
      </p>
      <h3>We Take A Different Approach</h3>
      <p>When you order a door from us, we don&#39;t just drop off a box and drive away. Our pricing includes everything you need to start enjoying your door on the day it arrives. This includes old door disposal, installation, hardware, even any modifications needed to the entryway to make your new door a safe, durable part of your home. Because our pricing is tailored to the size and conditions at each entryway, we need to visit your home &ndash; <strong>free of charge</strong> &ndash; to take measurements where you wish to have doors installed.</p>
      <p>After we measure, you will get access to a catalog of doors customized for your home, with all-inclusive pricing for each door.</p>
      </>}
      onClose={onCloseExplainerDialog}
      dismissButtonText={'Got it'}
    />
    <BwjViewManager
      state={viewState}
      loadingState={{
        message: 'One moment please...',
      }}
      errorState={errorState}
    >
      <StormDoorDetailDialog
        door={displayedDoorDetails}
        measurementRequestDate={measurementLastRequested}
        requestMeasurementFormModel={formModel}
        onMeasurementRequestSubmitted={onMeasurementRequestSubmitted}
        onShowPricingExplanation={() => setShowExplainerDialog(true)}
        onCloseDialog={onCloseDoorDetails}
      />
      <BwjPageHeader
        title={'Our Storm Doors'}
        subtitle={'Durably built, installation included'}
        actionButtons={[{
          id: 'btnHome',
          name: 'Return to home page',
          iconName: 'Home',
          disabled: false,
          onClick: () => window.location.href = 'https://bwjstorm.com',
        }]}
      />
      <BwjPageContent contentDescription={'primary content (no subcontent)'}>
        <ProductTileContainer>
          {doors.map(door =>
            <StormDoorTile
              key={door.id}
              idPrefix='sd-tile'
              door={door}
              onSelected={onViewDoorDetails}
            />
          )}
        </ProductTileContainer>
      </BwjPageContent>
    </BwjViewManager>
    </>
  );
};